import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import vpApprovalPriorityApi, { F90J } from '../../../services/vpApprovalPriorityApi';

type Mode = 'browse' | 'add' | 'edit';

export function useVpApprovalPriorityForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F90J[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [f90j_01, setF90j01] = useState('');
  const [f90j_11, setF90j11] = useState('');
  const [f90j_91, setF90j91] = useState('');
  const [f90j_92, setF90j92] = useState<string | null>(null);
  const [f90j_93, setF90j93] = useState('');

  // Employee name display (BR-V01/BR-U01)
  const [employeeName, setEmployeeName] = useState('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vpApprovalPriorityApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = useCallback((rec: F90J) => {
    setF90j01(rec.f90j_01 || '');
    setF90j11(rec.f90j_11 || '');
    setF90j91(rec.f90j_91 || '');
    setF90j92(rec.f90j_92);
    setF90j93(rec.f90j_93 || '');
    setEmployeeName('');
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const startAdd = useCallback(() => {
    setMode('add');
    setF90j01('');
    setF90j11('');
    setF90j91('');
    setF90j92(null);
    setF90j93('');
    setEmployeeName('');
  }, []);

  const startEdit = useCallback(() => {
    setMode('edit');
  }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  const buildPayload = useCallback((): Partial<F90J> => ({
    f90j_01: f90j_01.trim(),
    f90j_11: f90j_11.trim(),
    f90j_91: f90j_91,
    f90j_93: f90j_93,
  }), [f90j_01, f90j_11, f90j_91, f90j_93]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildPayload();
      if (mode === 'add') {
        await vpApprovalPriorityApi.create(payload);
        message.success('新增成功');
      } else {
        await vpApprovalPriorityApi.update(f90j_01, payload);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, f90j_01, buildPayload, loadRecords]);

  const doDelete = useCallback(async () => {
    if (!f90j_01) return;
    setLoading(true);
    try {
      await vpApprovalPriorityApi.delete(f90j_01);
      message.success('刪除成功');
      await loadRecords();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [f90j_01, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) => r.f90j_01.toUpperCase().startsWith(keyword.toUpperCase()));
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, records, currentIdx,
    f90j_01, f90j_11, f90j_91, f90j_92, f90j_93,
    setF90j01, setF90j11,
    employeeName, setEmployeeName,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
